import React, {useState} from "react";
import axios from "axios";
import {useNavigate} from "react-router-dom";
import {ArrowLeft, ArrowRight, AtSign, Eye, EyeOff, KeyRound, MessageCircle, Phone, ShieldCheck, User as UserIcon} from "lucide-react";
import {
  identifierAutoComplete,
  identifierInputMode,
  identifierKind,
  identifierLabel,
  normalizeIdentifier,
} from "../utils/identifier";
import full_icon from "../assets/yekkada-logo-new.png";
import * as Constants from "../utils/Constants";
import Button from "../components/ui/Button";
import TextField from "../components/ui/TextField";
import {cn} from "../utils/cn";

const FORGOT_URL = Constants.BASE_URL + "api/auth/v1/forgot-password";

/**
 * Forgot-password flow — identifier → WhatsApp OTP → new password.
 * Same three-step backend contract as before (send-otp / verify-otp /
 * reset) with `resetToken` carried between steps 2 and 3. Only the
 * presentation changes.
 */
const ForgotPassword = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [identifier, setIdentifier] = useState("");
  const [otp, setOtp] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [info, setInfo] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  // True when the backend reports the account has no verified phone/country
  // code on file — OTP delivery is impossible, so we show "contact admin"
  // guidance instead of a retryable error.
  const [noPhoneOnFile, setNoPhoneOnFile] = useState(false);
  const [loading, setLoading] = useState(false);
  // Terminal success state once the password has actually been changed.
  const [done, setDone] = useState(false);

  const sendOtp = async () => {
    setErrorMsg(""); setInfo(""); setNoPhoneOnFile(false);
    if (!identifier) { setErrorMsg("Enter your username, email or phone."); return; }
    try {
      setLoading(true);
      // Normalise to a canonical identifier (lower-case email, digits-
      // only phone with optional +) and include an identifierType hint.
      const value = normalizeIdentifier(identifier);
      const kind  = identifierKind(value);
      const res = await axios.post(`${FORGOT_URL}/send-otp`, {
        identifier: value,
        identifierType: kind === "empty" ? "username" : kind,
      });
      const masked = res.data?.responseData?.maskedPhone;
      setInfo(
        masked
          ? `If an account exists, an OTP was sent over WhatsApp to ${masked}.`
          : "If an account exists, an OTP has been sent over WhatsApp."
      );
      setStep(2);
    } catch (e) {
      const message = e?.response?.data?.responseMessage || "Could not send OTP. Try again later.";
      if (/contact.*admin/i.test(message)) {
        setNoPhoneOnFile(true);
      }
      setErrorMsg(message);
    } finally { setLoading(false); }
  };

  const verifyOtp = async () => {
    setErrorMsg("");
    if (!otp) { setErrorMsg("Enter the OTP you received."); return; }
    try {
      setLoading(true);
      // Send the normalised identifier so the backend's verify lookup
      // matches the one used for /send-otp above.
      const res = await axios.post(`${FORGOT_URL}/verify-otp`, {identifier: normalizeIdentifier(identifier), otp});
      setResetToken(res.data?.responseData?.resetToken || "");
      setInfo("");
      setStep(3);
    } catch (e) {
      setErrorMsg(e?.response?.data?.responseMessage || "Invalid or expired OTP.");
    } finally { setLoading(false); }
  };

  const resetPassword = async () => {
    setErrorMsg("");
    if (!newPassword || !confirmNewPassword) {
      setErrorMsg("Enter and confirm your new password."); return;
    }
    if (newPassword !== confirmNewPassword) {
      setErrorMsg("Passwords do not match."); return;
    }
    try {
      setLoading(true);
      await axios.post(`${FORGOT_URL}/reset`, {resetToken, newPassword, confirmNewPassword});
      setErrorMsg(""); setInfo("");
      setDone(true);
    } catch (e) {
      setErrorMsg(e?.response?.data?.responseMessage || "Could not reset password. Restart the flow.");
    } finally { setLoading(false); }
  };

  const onEnter = (fn) => (e) => { if (e.key === "Enter") fn(); };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-surface-muted">
      {/* BRAND PANEL */}
      <aside className="lg:flex-1 lg:max-w-[640px] bg-gradient-to-br from-brand-700 to-brand-900 text-white px-6 sm:px-10 py-8 lg:py-12 flex flex-col">
        <div className="w-fit bg-white rounded-lg px-3 py-1.5 shadow-sm">
          <img src={full_icon} alt="Yekkada" className="h-8 w-auto" />
        </div>
        <div className="flex-1 flex items-center">
          <div className="max-w-lg">
            <p className="text-xs uppercase tracking-wider font-semibold text-accent-300">Account recovery</p>
            <h1 className="text-3xl lg:text-4xl font-bold leading-tight mt-3">
              Forgot your password?<br />
              <span className="text-accent-400">We'll get you back in.</span>
            </h1>
            <p className="text-base text-white/80 mt-4 leading-relaxed">
              Verify your identity with a one-time code delivered over WhatsApp,
              then set a new password. Each code expires in a few minutes — we never
              store your password in plain text.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-8">
              <Feature icon={MessageCircle} title="OTP via WhatsApp" body="Sent to the verified phone number on the account." />
              <Feature icon={ShieldCheck} title="One-time reset token" body="Single-use, time-limited, server-side validated." />
            </div>
          </div>
        </div>
        <p className="text-xs text-white/60 mt-6">© 2026 Yekkada · Satyology Solutions Pvt Ltd</p>
      </aside>

      {/* FORM PANEL */}
      <section className="flex-1 px-6 sm:px-10 py-10 lg:py-12 flex items-center justify-center">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 text-sm">
            <button type="button" onClick={() => navigate("/")} className="inline-flex items-center gap-1 text-ink-500 hover:text-brand-700">
              <ArrowLeft className="h-4 w-4" /> Back to sign in
            </button>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-brand-700" />
            <h2 className="text-2xl font-bold text-ink-900">Reset password</h2>
          </div>
          <p className="text-base text-ink-500 mt-1">
            {done
              ? "All set — your password has been changed."
              : `Step ${step} of 3 — ${step === 1 ? "find your account" : step === 2 ? "verify the OTP" : "set a new password"}.`}
          </p>

          {/* Step indicator */}
          <div className="mt-4 flex items-center gap-2" aria-hidden="true">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={cn(
                  "h-1.5 flex-1 rounded-full",
                  s < step ? "bg-success-500" : s === step ? "bg-brand-500" : "bg-ink-200"
                )}
              />
            ))}
          </div>

          {info && (
            <div className="mt-5 rounded-lg bg-success-50 border border-success-100 px-4 py-3 text-sm text-success-700">
              {info}
            </div>
          )}
          {errorMsg && noPhoneOnFile && (
            <div className="mt-3 rounded-lg bg-warning-50 border border-warning-100 px-4 py-3 text-sm text-warning-800">
              <p className="font-semibold">No phone number on file</p>
              <p className="mt-1">{errorMsg} — we can't deliver a WhatsApp OTP without a verified phone number on the account.</p>
            </div>
          )}
          {errorMsg && !noPhoneOnFile && (
            <div className="mt-3 rounded-lg bg-danger-50 border border-danger-100 px-4 py-3 text-sm text-danger-700">
              {errorMsg}
            </div>
          )}

          <div className="mt-6 space-y-4">
            {step === 1 && (
              <>
                {(() => {
                  const kind = identifierKind(identifier);
                  const KindIcon =
                    kind === "email" ? AtSign : kind === "phone" ? Phone : UserIcon;
                  const showBadge = identifier.trim().length > 0;
                  return (
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <label htmlFor="forgot-identifier" className="block text-sm font-medium text-ink-700">
                          Username, email or phone <span className="text-danger-600">*</span>
                        </label>
                        {showBadge && (
                          <span className="inline-flex items-center gap-1 text-2xs font-semibold uppercase tracking-wider text-brand-700">
                            <KindIcon className="h-3 w-3" aria-hidden="true" />
                            {identifierLabel(kind)}
                          </span>
                        )}
                      </div>
                      <div className="relative">
                        <KindIcon
                          className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400 pointer-events-none"
                          aria-hidden="true"
                        />
                        <input
                          id="forgot-identifier"
                          value={identifier}
                          onChange={(e) => setIdentifier(e.target.value)}
                          onKeyDown={onEnter(sendOtp)}
                          placeholder="you@company.com · 98xxxxxxxx · username"
                          inputMode={identifierInputMode(kind)}
                          autoComplete={identifierAutoComplete(kind)}
                          spellCheck={false}
                          autoCapitalize="none"
                          autoFocus
                          className="block w-full h-9 pl-9 pr-3 text-base rounded-md bg-white border border-ink-200 shadow-inset text-ink-900 placeholder:text-ink-400 hover:border-ink-300 focus:border-brand-500 focus:outline-none focus:ring"
                        />
                      </div>
                      <p className="text-xs text-ink-500">
                        Use the same email, phone, or username you signed up with — the OTP goes to the verified phone on your account.
                      </p>
                    </div>
                  );
                })()}
                <Button variant="primary" size="lg" className="w-full" iconRight={ArrowRight} onClick={sendOtp} loading={loading}>
                  Send OTP
                </Button>
              </>
            )}

            {step === 2 && (
              <>
                <TextField
                  label="6-digit OTP"
                  required
                  inputMode="numeric"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                  onKeyDown={onEnter(verifyOtp)}
                  placeholder="123456"
                  autoFocus
                />
                <Button variant="primary" size="lg" className="w-full" iconRight={ArrowRight} onClick={verifyOtp} loading={loading}>
                  Verify OTP
                </Button>
                <Button variant="link" onClick={sendOtp} disabled={loading}>
                  Resend OTP
                </Button>
              </>
            )}

            {step === 3 && !done && (
              <>
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-ink-700">
                    New password <span className="text-danger-600">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Pick a strong password"
                      className={cn(
                        "block w-full h-9 pl-3 pr-10 text-base rounded-md",
                        "bg-white border border-ink-200 shadow-inset",
                        "text-ink-900 placeholder:text-ink-400",
                        "hover:border-ink-300",
                        "focus:border-brand-500 focus:outline-none focus:ring"
                      )}
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 inline-flex items-center justify-center rounded text-ink-400 hover:text-ink-600 hover:bg-ink-100"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <TextField
                  label="Confirm new password"
                  required
                  type={showPassword ? "text" : "password"}
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  onKeyDown={onEnter(resetPassword)}
                  placeholder="Re-enter the password"
                />
                <Button variant="primary" size="lg" className="w-full" onClick={resetPassword} loading={loading}>
                  Reset password
                </Button>
              </>
            )}

            {done && (
              <div className="space-y-4">
                <div className="rounded-lg bg-success-50 border border-success-100 px-4 py-3 text-sm text-success-700">
                  <p className="font-semibold">Password changed</p>
                  <p className="mt-1">You can now sign in with your new password.</p>
                </div>
                <Button variant="primary" size="lg" className="w-full" onClick={() => navigate("/", {replace: true})}>
                  Go to sign in
                </Button>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

function Feature({icon: Icon, title, body}) {
  return (
    <div className="flex items-start gap-3">
      <div className="h-9 w-9 rounded-lg bg-white/10 grid place-items-center shrink-0">
        <Icon className="h-4 w-4 text-accent-300" />
      </div>
      <div>
        <p className="text-sm font-semibold">{title}</p>
        <p className="text-xs text-white/70 mt-0.5 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}

export default ForgotPassword;
